import type { CSSProperties } from 'react'

export type BarChartVariant =
  | 'single'
  | 'double'
  | 'stacked'
  | 'single-horizontal'
  | 'stacked-horizontal'

export type BarChartSegment = {
  /** Relative weight of the segment (flex-grow). */
  value: number
  /** CSS color or design-system token. */
  color?: string
  /** Optional accessible name for the segment. */
  label?: string
}

/** One bar in a grouped / double vertical cluster. */
export type BarChartBar = {
  /** Fill amount 0–100 within the column (bottom-aligned). */
  value: number
  /** CSS color or design-system token. */
  color?: string
  /** Optional accessible / legend name. */
  label?: string
}

export const BAR_CHART_VARIANTS: BarChartVariant[] = [
  'single',
  'double',
  'stacked',
  'single-horizontal',
  'stacked-horizontal',
]

export const BAR_CHART_VARIANT_LABELS: Record<BarChartVariant, string> = {
  single: 'Single vertical bar',
  double: 'Double vertical bar',
  stacked: 'Stacked vertical bar',
  'single-horizontal': 'Single horizontal bar',
  'stacked-horizontal': 'Stacked horizontal bar',
}

export const BAR_CHART_DEFAULT_WIDTH = 56
export const BAR_CHART_DEFAULT_HEIGHT = 240
export const BAR_CHART_DEFAULT_TITLE = 'Title'
export const BAR_CHART_DEFAULT_VALUE_LABEL = '83%'

/** Vertical single fill (Figma blue/blue-600). */
export const BAR_CHART_SINGLE_COLOR = 'var(--ds-blue-600)'

/** Per-bar width in a double / grouped vertical cluster. */
export const BAR_CHART_DOUBLE_BAR_WIDTH = 24

/** Gap between bars in a double / grouped vertical cluster. */
export const BAR_CHART_DOUBLE_BAR_GAP = 4

/** Horizontal single fill (Figma blue/blue-500). */
export const BAR_CHART_HORIZONTAL_SINGLE_COLOR = 'var(--ds-blue-500)'

/** Horizontal bar thickness (Figma). */
export const BAR_CHART_HORIZONTAL_BAR_HEIGHT = 24

/**
 * Width of the bar + value group for single horizontal (Figma 240px).
 * Stacked horizontal grows to fill available space instead.
 */
export const BAR_CHART_HORIZONTAL_TRACK_GROUP_WIDTH = 240

export const BAR_CHART_HORIZONTAL_VALUE_WIDTH = 40

/** Total width for stacked horizontal (Figma). */
export const BAR_CHART_HORIZONTAL_STACKED_WIDTH = 286

/**
 * Default stacked vertical segments, top → bottom (Figma blue-300 → blue-600).
 */
export const BAR_CHART_DEFAULT_SEGMENTS: BarChartSegment[] = [
  { value: 1, color: 'var(--ds-blue-300)' },
  { value: 1, color: 'var(--ds-blue-400)' },
  { value: 1, color: 'var(--ds-blue-500)' },
  { value: 1, color: 'var(--ds-blue-600)' },
]

/** Default double vertical bars (side-by-side comparison). */
export const BAR_CHART_DEFAULT_DOUBLE_BARS: BarChartBar[] = [
  { value: 68, color: 'var(--ds-blue-400)', label: 'Previous' },
  { value: 92, color: 'var(--ds-blue-600)', label: 'Current' },
]

/**
 * Default stacked horizontal segments, left → right
 * (Figma purple-600, indigo-500, blue-600, cyan-500).
 */
export const BAR_CHART_HORIZONTAL_DEFAULT_SEGMENTS: BarChartSegment[] = [
  { value: 1, color: 'var(--ds-purple-600)' },
  { value: 1, color: 'var(--ds-indigo-500)' },
  { value: 1, color: 'var(--ds-blue-600)' },
  { value: 1, color: 'var(--ds-cyan-500)' },
]

type BarChartBaseProps = {
  /** Category / axis label. Defaults to "Title". */
  title?: string
  /** Hide the title label. */
  hideTitle?: boolean
  /** Dark surface — lightens labels for contrast. */
  darkMode?: boolean
  /**
   * Show hover tooltip with category + value(s).
   * Defaults to `true`.
   */
  showTooltip?: boolean
  /** Format tooltip numeric values. Defaults to String(value). */
  formatValue?: (value: number) => string
  /** Series name for single-bar tooltips. Defaults to "Value". */
  tooltipLabel?: string
  className?: string
  style?: CSSProperties
  'aria-label'?: string
}

type VerticalBarBaseProps = BarChartBaseProps & {
  /** Bar column width in px. Defaults to 56. */
  width?: number
  /** Bar column height in px. Defaults to 240. */
  height?: number
}

type HorizontalBarBaseProps = BarChartBaseProps & {
  /** Value label to the right of the bar. Defaults to "83%". */
  valueLabel?: string
  /** Hide the value label. */
  hideValue?: boolean
  /** Bar thickness in px. Defaults to 24. */
  barHeight?: number
}

export type SingleVerticalBarProps = VerticalBarBaseProps & {
  /** Bar fill color. Defaults to `--ds-blue-600`. */
  color?: string
  /**
   * Fill amount 0–100 within the column (bottom-aligned).
   * Defaults to 100 (full column, matching Figma).
   */
  value?: number
}

export type GroupedVerticalBarProps = Omit<VerticalBarBaseProps, 'width'> & {
  /** Side-by-side bars (typically 2). Values 0–100. */
  bars?: BarChartBar[]
  /** Width of each bar column in px. Defaults to 24. */
  barWidth?: number
  /** Gap between bars in px. Defaults to 4. */
  gap?: number
}

export type StackedVerticalBarProps = VerticalBarBaseProps & {
  /** Stacked segments, rendered top → bottom. */
  segments?: BarChartSegment[]
}

export type SingleHorizontalBarProps = HorizontalBarBaseProps & {
  /** Bar fill color. Defaults to `--ds-blue-500`. */
  color?: string
  /**
   * Width of the bar + value group in px. Defaults to 240.
   * The value column is 40px; the bar fills the remainder.
   */
  trackGroupWidth?: number
  /**
   * Fill amount 0–100 within the bar track (left-aligned).
   * Defaults to 100 (full track, matching Figma).
   */
  value?: number
}

export type StackedHorizontalBarProps = HorizontalBarBaseProps & {
  /** Stacked segments, rendered left → right. */
  segments?: BarChartSegment[]
  /** Fixed width for the whole control. Defaults to 286. */
  width?: number
}

export type BarChartProps =
  | (SingleVerticalBarProps & { variant?: 'single' })
  | (GroupedVerticalBarProps & { variant: 'double' })
  | (StackedVerticalBarProps & { variant: 'stacked' })
  | (SingleHorizontalBarProps & { variant: 'single-horizontal' })
  | (StackedHorizontalBarProps & { variant: 'stacked-horizontal' })
